/* Homepage-only presentation. This component never changes product or cart state. */
if (!customElements.get('reflective-home')) {
  customElements.define('reflective-home', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.switches = [...this.querySelectorAll('[data-rf-light-switch]')];
      this.pairs = [...this.querySelectorAll('[data-rf-pair]')];
      this.status = this.querySelector('[data-rf-status]');
      this.storageKey = 'reflective-light-state';
      this.fixedLight = this.dataset.fixedLight === 'off';
      this.state = this.fixedLight || this.dataset.light === 'off' ? 'off' : 'on';
      this.busy = false;
      this.classList.add('rf-home--enhanced');
      const signal = this.abort.signal;
      this.switches.forEach(button => {
        button.hidden = this.fixedLight;
        button.addEventListener('click', () => this.toggle(), { signal });
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && this.state === 'off') this.preparePair(entry.target).then(() => {
              if (this.isConnected && this.state === 'off') this.paintPair(entry.target, 'off');
            }).catch(() => {});
          });
        }, { rootMargin: '240px' });
        this.pairs.forEach(pair => this.observer.observe(pair));
      }
      this.addEventListener('click', event => {
        const menuLink = event.target.closest('.rf-mobile-nav a');
        if (menuLink) this.querySelector('.rf-mobile-nav').removeAttribute('open');
      }, { signal });
      const menu = this.querySelector('.rf-mobile-nav');
      if (menu) {
        const summary = menu.querySelector('summary');
        summary.setAttribute('aria-expanded', 'false');
        menu.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(menu.open)), { signal });
        document.addEventListener('click', event => {
          if (menu.open && !menu.contains(event.target)) menu.open = false;
        }, { signal });
        document.addEventListener('focusin', event => {
          if (menu.open && !menu.contains(event.target)) menu.open = false;
        }, { signal });
      }
      this.addEventListener('keydown', event => {
        const menu = this.querySelector('.rf-mobile-nav[open]');
        if (event.key === 'Escape' && menu) {
          menu.open = false;
          menu.querySelector('summary').focus();
        }
      }, { signal });
      this.setupVideo(signal);
      this.setupGallery(signal);
      try {
        if (this.fixedLight || this.dataset.light === 'off' || localStorage.getItem(this.storageKey) === 'off') this.restoreInitialOff();
      } catch (_) { if (this.state === 'off') this.restoreInitialOff(); }
    }

    restoreInitialOff() {
      // Restore presentation immediately; initial image decoding must not gate the palette.
      this.state = 'off';
      this.dataset.light = 'off';
      if (this.dataset.rfBootstrapped) document.documentElement.dataset.rfLight = 'off';
      this.pairs.forEach(pair => this.paintPair(pair, 'off'));
      this.switches.forEach(button => {
        button.setAttribute('aria-checked', 'false');
        button.setAttribute('aria-label', button.dataset.lightLabel);
        button.title = button.dataset.lightOn;
      });
      this.visiblePairs().forEach(pair => this.preparePair(pair).catch(() => {
        if (this.isConnected && this.state === 'off' && this.status) this.status.textContent = this.dataset.errorMessage;
      }));
    }

    visiblePairs() {
      return this.pairs.filter(pair => {
        const rect = pair.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > -240 && rect.top < window.innerHeight + 240;
      });
    }

    preparePair(pair) {
      if (pair.rfReady) return pair.rfReady;
      const off = pair.querySelector('[data-rf-off]');
      // An explicitly requested image must load even outside native lazy-load bounds.
      off.loading = 'eager';
      pair.rfReady = new Promise((resolve, reject) => {
        const cleanup = () => {
          clearTimeout(timer);
          off.removeEventListener('load', loaded);
          off.removeEventListener('error', failed);
        };
        const failed = () => { cleanup(); reject(new Error('Image unavailable')); };
        const loaded = async () => {
          try { if (off.decode) await off.decode(); } catch (_) { /* load success remains sufficient */ }
          cleanup();
          if (!off.naturalWidth) return reject(new Error('Image unavailable'));
          resolve();
        };
        const timer = setTimeout(failed, 10000);
        off.addEventListener('load', loaded, { once: true });
        off.addEventListener('error', failed, { once: true });
        off.src = off.dataset.src;
        if (off.complete && off.naturalWidth) loaded();
      }).catch(error => { pair.rfReady = null; throw error; });
      return pair.rfReady;
    }

    setupVideo(signal) {
      this.videos = [...this.querySelectorAll('video[data-rf-autoplay]')];
      if (!this.videos.length || !('IntersectionObserver' in window)) return;
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      const states = new Map(this.videos.map(video => [video, { inViewport: false, manuallyPaused: false, viewportPaused: false }]));
      const automaticPause = video => {
        const state = states.get(video);
        if (!video.paused) { state.viewportPaused = true; video.pause(); }
      };
      const automaticPlay = video => {
        const state = states.get(video);
        if (!state.inViewport || state.manuallyPaused || this.motionPreference.matches || document.hidden) return;
        video.play().then(() => {
          // Loading may finish after scrolling the video out of view.
          if (!this.isConnected || !state.inViewport || state.manuallyPaused || this.motionPreference.matches || document.hidden) automaticPause(video);
        }).catch(() => {});
      };
      this.videos.forEach(video => {
        const state = states.get(video);
        video.addEventListener('pause', () => {
          if (!state.viewportPaused && state.inViewport && !video.ended) state.manuallyPaused = true;
          state.viewportPaused = false;
        }, { signal });
        video.addEventListener('play', () => { state.manuallyPaused = false; }, { signal });
      });
      this.videoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          states.get(video).inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.3;
          if (states.get(video).inViewport) automaticPlay(video); else automaticPause(video);
        });
      }, { threshold: 0.3 });
      this.videos.forEach(video => this.videoObserver.observe(video));
      document.addEventListener('visibilitychange', () => {
        this.videos.forEach(video => document.hidden ? automaticPause(video) : automaticPlay(video));
      }, { signal });
      this.motionPreference.addEventListener('change', () => {
        this.videos.forEach(video => this.motionPreference.matches ? automaticPause(video) : automaticPlay(video));
      }, { signal });
    }

    setupGallery(signal) {
      const gallery = this.querySelector('[data-rf-gallery]');
      const button = this.querySelector('[data-rf-gallery-control]');
      if (!gallery || !button || !('IntersectionObserver' in window)) return;
      const slots = [...gallery.querySelectorAll('[data-rf-interior-slot]')];
      const sourceData = this.querySelector('[data-rf-interior-sources]');
      let imageSources;
      try {
        imageSources = JSON.parse(sourceData.textContent).map(source => ({ url: new URL(source.url, document.baseURI).href, color: source.color }));
        if (imageSources.some(source => !['white', 'gold', 'purple', 'red'].includes(source.color))) return;
      } catch (_) {
        // Invalid configuration keeps the initial photographs and hides rotation.
        return;
      }
      imageSources = [...new Map(imageSources.map(source => [source.url, source])).values()];
      if (imageSources.length <= slots.length) return;
      // Shuffle once per visit; least-recently-shown selection still avoids repeats.
      for (let i = imageSources.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [imageSources[i], imageSources[j]] = [imageSources[j], imageSources[i]];
      }
      const catalog = new Map(imageSources.map(source => [source.url, source]));
      let sequence = 0;
      const lastShown = new Map(imageSources.map(source => [source.url, 0]));
      slots.forEach(slot => lastShown.set(slot.querySelector('.is-active').src, ++sequence));
      const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
      let visible = false;
      let paused = false;
      let busy = false;
      let slotIndex = 0;
      const canRotate = () => this.isConnected && visible && !paused && !document.hidden && !preference.matches;
      const prepare = image => new Promise((resolve, reject) => {
        if (image.complete && image.naturalWidth) { resolve(); return; }
        let timer;
        const cleanup = () => { clearTimeout(timer); image.removeEventListener('load', loaded); image.removeEventListener('error', failed); signal.removeEventListener('abort', failed); };
        const loaded = async () => { cleanup(); try { if (image.decode) await image.decode(); } catch (_) {} image.naturalWidth ? resolve() : reject(); };
        const failed = () => { cleanup(); reject(); };
        image.addEventListener('load', loaded, { once: true });
        image.addEventListener('error', failed, { once: true });
        signal.addEventListener('abort', failed, { once: true });
        timer = setTimeout(failed, 10000);
        if (!image.src) image.src = image.dataset.src;
        if (image.complete && image.naturalWidth) loaded();
      });
      const initializeFrames = async () => {
        busy = true;
        const colors = new Set();
        const initial = imageSources.filter(source => {
          if (colors.has(source.color)) return false;
          colors.add(source.color);
          return true;
        }).slice(0, slots.length);
        try {
          if (initial.length !== slots.length) return;
          const images = initial.map(source => {
            const image = new Image();
            image.src = source.url;
            return image;
          });
          await Promise.all(images.map(prepare));
          if (signal.aborted || !this.isConnected || paused) return;
          // Commit the complete, decoded set together; HTML remains a safe fallback.
          imageSources.forEach(source => lastShown.set(source.url, 0));
          slots.forEach((slot, index) => {
            const active = slot.querySelector('.is-active');
            active.src = images[index].src;
            active.dataset.rfColor = initial[index].color;
            lastShown.set(initial[index].url, ++sequence);
          });
          gallery.dataset.rfInitialReady = 'true';
        } catch (_) { /* Keep all fallback frames if any initial image fails. */ }
        finally { busy = false; }
      };
      const rotate = async () => {
        if (!canRotate() || busy) return;
        busy = true;
        const visibleSlots = slots.filter(item => item.getClientRects().length > 0);
        if (!visibleSlots.length) { busy = false; return; }
        const slot = visibleSlots[slotIndex++ % visibleSlots.length];
        const active = slot.querySelector('.is-active');
        const next = [...slot.querySelectorAll('img')].find(image => image !== active);
        const shown = new Set(visibleSlots.map(item => item.querySelector('.is-active').src));
        const otherColors = new Set(visibleSlots.filter(item => item !== slot).map(item => catalog.get(item.querySelector('.is-active').src)?.color));
        const candidate = imageSources.filter(source => !shown.has(source.url) && !otherColors.has(source.color))
          .sort((a, b) => lastShown.get(a.url) - lastShown.get(b.url))[0];
        const requested = candidate?.url;
        if (!requested) { busy = false; return; }
        next.dataset.src = requested;
        next.src = requested;
        try {
          await prepare(next);
          if (!canRotate()) return;
          // Recheck colours after loading: a resize can expose the third slot.
          if (slots.some(item => item !== slot && item.getClientRects().length > 0 && catalog.get(item.querySelector('.is-active').src)?.color === candidate.color)) return;
          // Keep all three slots distinct so a resize never exposes repeated colours.
          slots.filter(item => item.getClientRects().length === 0).forEach(item => {
            const hiddenActive = item.querySelector('.is-active');
            if (catalog.get(hiddenActive.src)?.color === candidate.color) {
              hiddenActive.src = active.src;
              hiddenActive.dataset.rfColor = catalog.get(active.src).color;
            }
          });
          next.dataset.rfColor = candidate.color;
          active.classList.remove('is-active');
          active.setAttribute('aria-hidden', 'true');
          next.classList.add('is-active');
          next.setAttribute('aria-hidden', 'false');
          lastShown.set(requested, ++sequence);
        } catch (_) { /* A failed alternate leaves the current photograph in place. */ }
        finally { busy = false; }
      };
      const sync = () => {
        clearInterval(this.galleryTimer);
        button.hidden = preference.matches;
        if (canRotate()) this.galleryTimer = setInterval(rotate, 2000);
      };
      button.addEventListener('click', () => {
        paused = !paused;
        button.setAttribute('aria-pressed', String(paused));
        button.textContent = paused ? this.dataset.galleryResume : this.dataset.galleryPause;
        sync();
      }, { signal });
      this.galleryObserver = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: .15 });
      this.galleryObserver.observe(gallery);
      document.addEventListener('visibilitychange', sync, { signal });
      preference.addEventListener('change', sync, { signal });
      initializeFrames();
      sync();
    }

    paintPair(pair, state) {
      pair.dataset.light = state;
      pair.querySelector('.rf-light-pair__on').setAttribute('aria-hidden', state === 'off' ? 'true' : 'false');
      pair.querySelector('.rf-light-pair__off').setAttribute('aria-hidden', state === 'off' ? 'false' : 'true');
    }

    async prepareLitImage(pair) {
      const image = pair.querySelector('.rf-light-pair__on');
      image.loading = 'eager';
      let timer;
      try {
        if (image.decode) {
          await Promise.race([
            image.decode(),
            new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Image unavailable')), 10000); })
          ]);
        }
        if (!image.naturalWidth) throw new Error('Image unavailable');
      } finally { clearTimeout(timer); }
    }

    toggle() {
      if (!this.busy) this.setState(this.state === 'on' ? 'off' : 'on', true);
    }

    async setState(next, remember) {
      if (this.fixedLight) { next = 'off'; remember = false; }
      this.busy = true;
      this.switches.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      try {
        const visible = this.visiblePairs();
        if (!this.fixedLight) await Promise.all(visible.map(pair => this.prepareLitImage(pair)));
        if (next === 'off') await Promise.all(visible.map(pair => this.preparePair(pair)));
        if (!this.isConnected) return;
        this.state = next;
        this.dataset.light = next;
        if (this.dataset.rfBootstrapped) document.documentElement.dataset.rfLight = next;
        this.pairs.forEach(pair => {
          if (next === 'on') this.paintPair(pair, next);
          else if (pair.querySelector('[data-rf-off]').naturalWidth) this.paintPair(pair, next);
        });
        // Scrolling during the awaited preload can expose pairs after the observer's first callback.
        if (next === 'off') this.visiblePairs().forEach(pair => {
          this.preparePair(pair).then(() => {
            if (this.isConnected && this.state === 'off') this.paintPair(pair, 'off');
          }).catch(() => {
            if (this.isConnected && this.state === 'off' && this.status) this.status.textContent = this.dataset.errorMessage;
          });
        });
        this.switches.forEach(button => {
          button.setAttribute('aria-checked', next === 'on' ? 'true' : 'false');
          button.setAttribute('aria-label', button.dataset.lightLabel);
          button.title = next === 'on' ? button.dataset.lightOff : button.dataset.lightOn;
        });
        if (this.status) this.status.textContent = next === 'on' ? this.dataset.onMessage : this.dataset.offMessage;
        if (remember) {
          try { localStorage.setItem(this.storageKey, next); } catch (_) { /* Optional persistence. */ }
        }
      } catch (_) {
        if (this.status) this.status.textContent = this.dataset.errorMessage;
      } finally {
        this.busy = false;
        this.switches.forEach(button => { button.disabled = false; button.removeAttribute('aria-busy'); });
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.videoObserver?.disconnect();
      this.videos?.forEach(video => video.pause());
      this.galleryObserver?.disconnect();
      clearInterval(this.galleryTimer);
      this.initialized = false;
    }
  });
}
